import React from "react";
import Toaster from "../Toaster";
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from "../CclDataTable";
import EmailEditor from "../emaileditor/EmailEditor";
import QcQuickNote from "../../code/models/QcQuickNote";
import ApiQc from "../../code/api/ApiQc";

import "../../css/adminqcquicknotes.css";
import ButtonConfirm, { ButtonConfirmSize } from "../ButtonConfirm";

export default function AdminQCQuickNotesForm() {
    const [toastMessage, setToastMessage] = React.useState<string>("");

    const [quickNotes, setQuickNotes] = React.useState<QcQuickNote[]>([]);
    const [selectedQuickNote, setSelectedQuickNote] = React.useState<QcQuickNote | null>(null);
    const [editQuickNote, setEditQuickNote] = React.useState<QcQuickNote | null>(null);
    const [anyChanges, setAnyChanges] = React.useState<boolean>(false);

    React.useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        ApiQc.getQuickNotes().then((qNotes) => {
            setQuickNotes(qNotes);
            addNewClick();
        });
    }

    function rowSelected(rowData: QcQuickNote): void {
        setSelectedQuickNote(rowData);
        setEditQuickNote(structuredClone(rowData));
        setAnyChanges(false);
    }

    function emailEditorTextChanged(newText: string) {
        if (editQuickNote !== null) {
            editQuickNote.note = newText;
        }

        checkHasChanges();
    }

    function checkHasChanges() {
        var isAnyChanges = editQuickNote !== null &&
            selectedQuickNote !== null &&
            (selectedQuickNote.name !== editQuickNote.name ||
                selectedQuickNote.groupName !== editQuickNote.groupName ||
                selectedQuickNote.note !== editQuickNote.note);
        setAnyChanges(isAnyChanges);
    }

    const bplColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "groupName",
            title: "Group",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "name",
            title: "Name",
            visible: true,
            type: CclDataTableColumnType.Data
        }
    ];

    function saveQuickNoteClick(): void {
        if (anyChanges && !!editQuickNote && !!selectedQuickNote) {
            let isNew = !editQuickNote.qcQuickNoteId;
            ApiQc.updateQuickNote(editQuickNote).then((savedNote) => {
                if (!!savedNote) {
                    selectedQuickNote.qcQuickNoteId = savedNote.qcQuickNoteId;
                    selectedQuickNote.companyId = savedNote.companyId;
                    selectedQuickNote.groupName = savedNote.groupName;
                    selectedQuickNote.name = savedNote.name;
                    selectedQuickNote.note = savedNote.note;

                    setToastMessage(`Quick Note "${editQuickNote.name}" saved.`);

                    setTimeout(() => {
                        rowSelected(selectedQuickNote);
                    }, 1000);

                    if (isNew) {
                        setQuickNotes([...quickNotes, selectedQuickNote])
                    }
                }
            });
        }
    }

    function deleteQuickNoteConfirm(isConfirmed: boolean): void {
        if (isConfirmed && !!editQuickNote && !!editQuickNote.qcQuickNoteId) {
            ApiQc.deleteQuickNote(editQuickNote.qcQuickNoteId).then((result) => {
                if (result) {
                    loadData();
                }
            });
        }
    }

    function addNewClick(): void {
        setSelectedQuickNote(new QcQuickNote());
        setEditQuickNote(new QcQuickNote());
        setAnyChanges(false);
    }

    return (
        <React.Fragment>
            <div className="quicknotes-main">
                <div className="quicknotes-left">
                    <div className="quicknotes-button">
                        <button className="btn btn-sm btn-outline-primary" disabled={!editQuickNote?.qcQuickNoteId} onClick={addNewClick} >Add Template</button>
                    </div>
                    <div className="quicknotes-table-wrapper">
                        <div>
                            <CclDataTable key={"aqcqndt" + selectedQuickNote?.qcQuickNoteId}
                                name={"profilesTable"}
                                tableData={quickNotes || []}
                                columnDefinitions={bplColumnDefs}
                                onRowSelect={rowSelected}
                                rowSelect={true}
                                onButtonClick={null}
                                defaultSelectedRow={selectedQuickNote}
                            />
                        </div>
                    </div>
                </div>
                <div className="quicknotes-right" key={"et-edit-" + (selectedQuickNote?.qcQuickNoteId || "a")}>
                    <div className="row quicknotes-editortop">
                        <div className="col-md-3">
                            <label htmlFor="templateSubject" className="form-label">Group</label>
                            <input className="form-control" disabled={!editQuickNote}
                                id="templateSubject" defaultValue={selectedQuickNote?.groupName} autoComplete={"off"}
                                onChange={(e) => {
                                    if (!!editQuickNote) {
                                        editQuickNote.groupName = e.target.value;
                                        checkHasChanges();
                                    }
                                }}></input>
                        </div>
                        <div className="col-md-4">
                            <label htmlFor="templateSendgridId" className="form-label text-nowrap">Name</label>
                            <input className="form-control" disabled={!editQuickNote}
                                id="templateSendgridId" defaultValue={selectedQuickNote?.name} autoComplete={"off"}
                                onChange={(e) => {
                                    if (!!editQuickNote) {
                                        editQuickNote.name = e.target.value;
                                        checkHasChanges();
                                    }
                                }}></input>
                        </div>
                        <div className="col-1 quicknotes-actionbuttons text-nowrap">
                            <button className={"btn btn-sm " + ((!editQuickNote || !anyChanges) ? "btn-outline-secondary" : "btn-primary")}
                                disabled={!editQuickNote || !anyChanges} onClick={saveQuickNoteClick} >Save Quick Note</button>
                            {!!editQuickNote && !!editQuickNote.qcQuickNoteId &&
                                <ButtonConfirm cssClass="btn-outline-danger quicknotes-deletebutton" enabled={true}
                                    buttonText={"Delete Quick Note"} confirmTitle={"Quick Note"} confirmMessage={"Delete Quick Note?"}
                                    buttonSize={ButtonConfirmSize.Small} onConfirm={deleteQuickNoteConfirm} />}
                        </div>
                    </div>
                    <div className="row quicknotes-editormain">
                        {!!editQuickNote &&
                            <EmailEditor initialValue={editQuickNote.note} onChange={(newText) => emailEditorTextChanged(newText)} previewMode={false} />
                        }
                    </div>
                </div>
            </div>
            <Toaster title="QC Quick Notes" subtitle="info" message={toastMessage} key={"emailtemplatetoast"} />
        </React.Fragment>
    )
}
